import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTimes,
  faGripVertical,
  faPlus,
  faTrash,
  faCog,
} from "@fortawesome/free-solid-svg-icons";
import "./styles/QuotaModal.css";

const QuotaModal = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  sdataGroup,
  searchVariables,
}) => {
  // Clean initial data to remove nested allQuotas
  const cleanInitialData = (data) => {
    if (!data) return { quotas: [], nextQuotaNumber: 2 };

    // If data has allQuotas, convert to array format
    if (data.allQuotas && typeof data.allQuotas === "object") {
      const quotas = Object.entries(data.allQuotas).map(([name, config]) => {
        // Remove nested allQuotas if they exist
        const { allQuotas, ...cleanConfig } = config;
        return {
          ...cleanConfig,
          quotaName: name,
        };
      });

      return {
        quotas,
        nextQuotaNumber: data.nextQuotaNumber || quotas.length + 1,
      };
    }

    // If data has quotas array already
    if (Array.isArray(data.quotas)) {
      return {
        quotas: data.quotas.map((quota) => {
          const { allQuotas, ...cleanConfig } = quota;
          return cleanConfig;
        }),
        nextQuotaNumber: data.nextQuotaNumber || data.quotas.length + 1,
      };
    }

    return { quotas: [], nextQuotaNumber: 2 };
  };

  const [quotaState, setQuotaState] = useState({
    quotas: [], // Array of quota configurations
    nextQuotaNumber: 2,
    currentQuotaIndex: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [activeTab, setActiveTab] = useState("variables");
  const searchRef = useRef(null);

  // Initialize with cleaned data
  useEffect(() => {
    if (isOpen) {
      const cleanedData = cleanInitialData(initialData);
      // console.log ("Cleaned initial data:", cleanedData);

      // If no quotas exist, create default quota1 with isQuota variables
      if (cleanedData.quotas.length === 0) {
        const quotaVariables = [];
        Object.entries(sdataGroup).forEach(([key, question]) => {
          if (question.oeparameters?.isQuota) {
            quotaVariables.push({
              id: key,
              name: question.Qtitle || key,
              questionNumber: question.Question_number || key,
              type: question.type || "Unknown",
              role: "none",
            });
          }
        });

        const defaultQuota = {
          variables: quotaVariables,
          quotaTable: null,
          quotaName: "quota1",
        };

        setQuotaState({
          quotas: [defaultQuota],
          nextQuotaNumber: 2,
          currentQuotaIndex: 0,
        });
      } else {
        setQuotaState({
          ...cleanedData,
          currentQuotaIndex: 0,
        });
      }
    }
  }, [isOpen, initialData, sdataGroup]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCurrentQuota = () => {
    return quotaState.quotas[quotaState.currentQuotaIndex] || null;
  };

  const updateCurrentQuota = (updates) => {
    const newQuotas = [...quotaState.quotas];
    const currentIndex = quotaState.currentQuotaIndex;

    if (!newQuotas[currentIndex]) {
      newQuotas[currentIndex] = {
        quotaName: `quota${currentIndex + 1}`,
        variables: [],
        quotaTable: null,
      };
    }

    newQuotas[currentIndex] = {
      ...newQuotas[currentIndex],
      ...updates,
    };

    setQuotaState((prev) => ({
      ...prev,
      quotas: newQuotas,
    }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term.trim() === "") {
      setSearchResults([]);
      return;
    }
    const results = searchVariables(term);
    setSearchResults(results);
    setShowSearchResults(true);
  };

  const handleDragVariable = (result) => {
    if (!result.destination) return;

    const currentQuota = getCurrentQuota();
    if (!currentQuota) return;

    const items = Array.from(currentQuota.variables || []);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    updateCurrentQuota({ variables: items });
  };

  const handleAddVariable = (variable) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota) return;

    if (!currentQuota.variables?.some((v) => v.id === variable.id)) {
      const newVariable = {
        ...variable,
        role: "none",
        settings: {},
      };

      const updatedVariables = [...(currentQuota.variables || []), newVariable];
      updateCurrentQuota({ variables: updatedVariables });
    }
    setSearchTerm("");
    setShowSearchResults(false);
  };

  const handleRemoveVariable = (variableId) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota) return;

    const updatedVariables =
      currentQuota.variables?.filter((v) => v.id !== variableId) || [];
    updateCurrentQuota({ variables: updatedVariables });
  };

  const handleVariableRoleChange = (variableId, role) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota) return;

    const updatedVariables =
      currentQuota.variables?.map((v) =>
        v.id === variableId ? { ...v, role, settings: {} } : v
      ) || [];

    updateCurrentQuota({ variables: updatedVariables });
  };

  const handleAddNewQuota = () => {
    const newQuotaName = `quota${quotaState.nextQuotaNumber}`;
    const newQuota = {
      variables: [],
      quotaTable: null,
      quotaName: newQuotaName,
    };

    const newQuotas = [...quotaState.quotas, newQuota];

    setQuotaState((prev) => ({
      ...prev,
      quotas: newQuotas,
      nextQuotaNumber: prev.nextQuotaNumber + 1,
      currentQuotaIndex: newQuotas.length - 1,
    }));

    setActiveTab("variables");
  };

  const handleSwitchQuota = (index) => {
    setQuotaState((prev) => ({
      ...prev,
      currentQuotaIndex: index,
    }));
    setActiveTab("variables");
  };

  const handleGenerateQuotaTable = () => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota) return;

    const rowVariables =
      currentQuota.variables?.filter((v) => v.role === "row") || [];
    const columnVariables =
      currentQuota.variables?.filter((v) => v.role === "column") || [];
    const level2Variables =
      currentQuota.variables?.filter((v) => v.role === "level2") || [];

    if (rowVariables.length === 0) {
      alert("Please assign at least one variable to rows");
      return;
    }

    // Get question data from sdataGroup
    const rows = [];
    const columns = [];
    const level2Structure = {};

    // Generate rows from row variables
    rowVariables.forEach((variable) => {
      const question = sdataGroup[variable.id];
      if (!question || !question.rows) {
        console.warn(`Question ${variable.id} not found or has no rows`);
        return;
      }

      question.rows.forEach((row) => {
        rows.push({
          id: `${variable.id}_${row.index || row.id}`,
          label: row.text || `Row ${row.index || row.id}`,
          variableId: variable.id,
          variableLabel: variable.name || variable.questionNumber,
          rowIndex: row.index || row.id,
          originalText: row.text,
        });
      });
    });

    // Add total row
    if (rows.length > 0) {
      rows.push({
        id: "total",
        label: "Total",
        isTotal: true,
      });
    }

    // Generate columns from column variables
    columnVariables.forEach((variable) => {
      const question = sdataGroup[variable.id];
      if (!question) {
        console.warn(`Question ${variable.id} not found`);
        return;
      }

      if (!question.rows || question.rows.length === 0) {
        // If no rows, add as a single column
        columns.push({
          id: `${variable.id}`,
          label: variable.name || variable.questionNumber,
          variableId: variable.id,
          isSingle: true,
        });
        return;
      }

      question.rows.forEach((row) => {
        columns.push({
          id: `${variable.id}_${row.index || row.id}`,
          label: row.text || `Column ${row.index || row.id}`,
          variableId: variable.id,
          variableLabel: variable.name || variable.questionNumber,
          rowIndex: row.index || row.id,
          originalText: row.text,
        });
      });
    });

    // If no columns selected, add default "Total" column
    if (columns.length === 0 && rows.length > 0) {
      columns.push({
        id: "col_total",
        label: "Total",
        isTotal: true,
        isDefault: true,
      });
    } else if (columns.length > 0) {
      // Add total column at the end
      columns.push({
        id: "col_total",
        label: "Total",
        isTotal: true,
      });
    }

    // Generate level2 columns if any
    level2Variables.forEach((variable) => {
      const question = sdataGroup[variable.id];
      if (!question || !question.rows) {
        console.warn(`Question ${variable.id} not found or has no rows`);
        return;
      }

      const level2Rows = [];
      question.rows.forEach((row) => {
        level2Rows.push({
          id: `${variable.id}_${row.index || row.id}`,
          label: row.text || `Level2 ${row.index || row.id}`,
          variableId: variable.id,
          variableLabel: variable.name || variable.questionNumber,
          rowIndex: row.index || row.id,
        });
      });

      // For each base column (except totals), create level2 columns
      columns.forEach((col) => {
        if (!col.isTotal && !col.isDefault && col.id !== "col_total") {
          level2Structure[col.id] = level2Rows;
        }
      });
    });

    // Initialize data object with zeros for all cells
    const data = {};

    if (rows.length > 0 && columns.length > 0) {
      // For simple row-only structure
      if (columns.length === 1 && columns[0].isDefault) {
        rows.forEach((row) => {
          if (!row.isTotal) {
            const key = `${row.id}_col_total`;
            data[key] = 0;
          }
        });
      } else {
        // For grid structures
        rows.forEach((row) => {
          if (row.isTotal) return;

          columns.forEach((col) => {
            if (col.isTotal || col.isDefault) return;

            if (level2Structure[col.id]) {
              level2Structure[col.id].forEach((level2Col) => {
                const key = `${row.id}_${level2Col.id}`;
                data[key] = 0;
              });
            } else {
              const key = `${row.id}_${col.id}`;
              data[key] = 0;
            }
          });
        });
      }
    }

    // Build table structure
    const quotaTable = {
      rows,
      columns,
      level2Columns:
        Object.keys(level2Structure).length > 0 ? level2Structure : null,
      data,
    };

    updateCurrentQuota({ quotaTable });
    setActiveTab("configuration");
  };

  const handleQuotaValueChange = (rowId, colId, value) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota?.quotaTable) return;

    const newData = { ...(currentQuota.quotaTable.data || {}) };
    const key = `${rowId}_${colId}`;
    const numValue = parseInt(value) || 0;
    newData[key] = numValue;

    const updatedTable = {
      ...currentQuota.quotaTable,
      data: newData,
    };

    updateCurrentQuota({ quotaTable: updatedTable });
  };

  const calculateColumnTotal = (colId) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota?.quotaTable?.rows) return 0;

    return currentQuota.quotaTable.rows.reduce((sum, row) => {
      if (row.isTotal) return sum;
      const key = `${row.id}_${colId}`;
      return sum + (currentQuota.quotaTable.data[key] || 0);
    }, 0);
  };

  const calculateRowTotal = (rowId) => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota?.quotaTable?.columns) return 0;

    return currentQuota.quotaTable.columns.reduce((sum, col) => {
      if (col.isTotal || col.isDefault) return sum;
      const key = `${rowId}_${col.id}`;
      return sum + (currentQuota.quotaTable.data[key] || 0);
    }, 0);
  };

  const calculateGrandTotal = () => {
    const currentQuota = getCurrentQuota();
    if (!currentQuota?.quotaTable?.rows || !currentQuota?.quotaTable?.columns)
      return 0;

    return currentQuota.quotaTable.rows.reduce((rowSum, row) => {
      if (row.isTotal) return rowSum;
      return (
        rowSum +
        currentQuota.quotaTable.columns.reduce((colSum, col) => {
          if (col.isTotal || col.isDefault) return colSum;
          const key = `${row.id}_${col.id}`;
          return colSum + (currentQuota.quotaTable.data[key] || 0);
        }, 0)
      );
    }, 0);
  };

  const handleSaveAllQuotas = () => {
    // Prepare clean data structure without nested allQuotas
    const finalData = {
      quotas: quotaState.quotas.map((quota) => ({
        ...quota,
        // Ensure no nested allQuotas
        allQuotas: undefined,
      })),
      nextQuotaNumber: quotaState.nextQuotaNumber,
    };

    // console.log ("Saving clean quota data:", finalData);
    onSave(finalData);
  };

  const getQuestionRowCount = (variableId) => {
    const question = sdataGroup[variableId];
    if (!question || !question.rows) return 0;
    return question.rows.length;
  };

  const getQuestionInfo = (variableId) => {
    const question = sdataGroup[variableId];
    if (!question) return null;
    return {
      rows: question.rows || [],
      columns: question.columns || [],
      type: question.type || "Unknown",
    };
  };

  if (!isOpen) return null;

  const currentQuota = getCurrentQuota();
  if (!currentQuota) return null;

  const quotaNames = quotaState.quotas.map((q) => q.quotaName);

  return (
    <div className="quota-modal-overlay">
      <div className="quota-modal-content">
        <div className="quota-modal-header">
          <div className="quota-header-left">
            <h3>Quota Configuration</h3>
            <div className="quota-selector">
              <select
                value={quotaState.currentQuotaIndex}
                onChange={(e) => handleSwitchQuota(parseInt(e.target.value))}
                className="quota-dropdown"
              >
                {quotaNames.map((name, index) => (
                  <option key={index} value={index}>
                    {name}
                  </option>
                ))}
              </select>
              <button onClick={handleAddNewQuota} className="quota-add-button">
                <FontAwesomeIcon icon={faPlus} /> New Quota
              </button>
            </div>
          </div>
          <button onClick={onClose} className="quota-modal-close-button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="quota-modal-tabs">
          <button
            className={`quota-tab-button ${
              activeTab === "variables" ? "active" : ""
            }`}
            onClick={() => setActiveTab("variables")}
          >
            Variables
          </button>
          <button
            className={`quota-tab-button ${
              activeTab === "configuration" ? "active" : ""
            }`}
            onClick={() => setActiveTab("configuration")}
            disabled={!currentQuota.quotaTable}
          >
            {currentQuota.quotaName} Table
          </button>
        </div>

        <div className="quota-modal-body">
          {activeTab === "variables" && (
            <>
              <div className="current-quota-banner">
                <h4>
                  Configuring:{" "}
                  <span className="quota-name-highlight">
                    {currentQuota.quotaName}
                  </span>
                </h4>
                <p className="quota-instructions">
                  Assign variables to rows, columns, or nested columns.
                  Variables will read rows/columns from the survey data.
                </p>
              </div>

              {/* Search and Variables Section */}
              <div className="quota-section">
                <h4>Add Variables</h4>
                <div className="quota-search-container" ref={searchRef}>
                  <div className="quota-search-input-wrapper">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="quota-search-icon"
                    />
                    <input
                      type="text"
                      placeholder="Search variables from survey..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="quota-search-input"
                    />
                  </div>
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="quota-search-results">
                      {searchResults.map((variable, index) => (
                        <div
                          key={index}
                          className="quota-search-result-item"
                          onClick={() => handleAddVariable(variable)}
                        >
                          <strong>{variable.questionNumber}</strong>:{" "}
                          {variable.name}
                          <span className="quota-variable-type">
                            {variable.type}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Draggable Variables List */}
                <DragDropContext onDragEnd={handleDragVariable}>
                  <Droppable droppableId="variables">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="quota-variables-list"
                      >
                        {!currentQuota.variables ||
                        currentQuota.variables.length === 0 ? (
                          <div className="quota-empty-state">
                            <p>
                              No variables added yet. Search and add variables
                              to configure your quota.
                            </p>
                            <p className="quota-empty-hint">
                              <strong>Tip:</strong> Variables with{" "}
                              <code>isQuota: true</code> in oeParameters are
                              added automatically.
                            </p>
                          </div>
                        ) : (
                          currentQuota.variables.map((variable, index) => {
                            const questionInfo = getQuestionInfo(variable.id);
                            const rowCount = getQuestionRowCount(variable.id);

                            return (
                              <Draggable
                                key={variable.id}
                                draggableId={variable.id}
                                index={index}
                              >
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className="quota-variable-item"
                                    style={provided.draggableProps.style}
                                  >
                                    <div
                                      {...provided.dragHandleProps}
                                      className="quota-drag-handle"
                                    >
                                      <FontAwesomeIcon icon={faGripVertical} />
                                    </div>
                                    <div className="quota-variable-info">
                                      <span className="quota-variable-text">
                                        <strong>
                                          {variable.questionNumber ||
                                            variable.id}
                                        </strong>
                                        : {variable.name}
                                      </span>
                                      <span className="quota-variable-type-badge">
                                        {variable.type}
                                      </span>
                                    </div>

                                    <div className="quota-variable-role-selector">
                                      <select
                                        value={variable.role}
                                        onChange={(e) =>
                                          handleVariableRoleChange(
                                            variable.id,
                                            e.target.value
                                          )
                                        }
                                        className="quota-role-select"
                                      >
                                        <option value="none">
                                          -- Select Role --
                                        </option>
                                        <option value="row">
                                          Rows ({rowCount} items)
                                        </option>
                                        <option value="column">
                                          Columns ({rowCount} items)
                                        </option>
                                        <option value="level2">
                                          Nested Columns ({rowCount} items)
                                        </option>
                                      </select>
                                      {questionInfo && (
                                        <div className="quota-question-preview">
                                          <small>
                                            {rowCount} answer options available
                                          </small>
                                        </div>
                                      )}
                                    </div>

                                    <button
                                      onClick={() =>
                                        handleRemoveVariable(variable.id)
                                      }
                                      className="quota-remove-button"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {currentQuota.variables &&
                  currentQuota.variables.length > 0 && (
                    <div className="quota-generate-button-container">
                      <button
                        onClick={handleGenerateQuotaTable}
                        className="quota-generate-button"
                        disabled={
                          !currentQuota.variables.some((v) => v.role === "row")
                        }
                      >
                        Generate {currentQuota.quotaName} Table
                      </button>
                      {!currentQuota.variables.some(
                        (v) => v.role === "row"
                      ) && (
                        <p className="quota-error-message">
                          Please assign at least one variable to "Rows"
                        </p>
                      )}
                    </div>
                  )}
              </div>

              {/* Variable Summary */}
              {currentQuota.variables && currentQuota.variables.length > 0 && (
                <div className="quota-section">
                  <h4>Variable Summary for {currentQuota.quotaName}</h4>
                  <div className="quota-summary-grid">
                    <div className="quota-summary-card">
                      <h5>
                        Rows (
                        {
                          currentQuota.variables.filter((v) => v.role === "row")
                            .length
                        }
                        )
                      </h5>
                      {currentQuota.variables.filter((v) => v.role === "row")
                        .length === 0 ? (
                        <div className="quota-summary-empty">
                          No variables assigned to rows
                        </div>
                      ) : (
                        currentQuota.variables
                          .filter((v) => v.role === "row")
                          .map((v) => (
                            <div key={v.id} className="quota-summary-item">
                              <strong>{v.questionNumber || v.id}</strong>:{" "}
                              {v.name}
                              <div className="quota-summary-details">
                                Will create {getQuestionRowCount(v.id)} rows
                                from survey data
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                    <div className="quota-summary-card">
                      <h5>
                        Columns (
                        {
                          currentQuota.variables.filter(
                            (v) => v.role === "column"
                          ).length
                        }
                        )
                      </h5>
                      {currentQuota.variables.filter((v) => v.role === "column")
                        .length === 0 ? (
                        <div className="quota-summary-empty">
                          No variables assigned to columns
                          <div className="quota-summary-hint">
                            (A default "Total" column will be created)
                          </div>
                        </div>
                      ) : (
                        currentQuota.variables
                          .filter((v) => v.role === "column")
                          .map((v) => (
                            <div key={v.id} className="quota-summary-item">
                              <strong>{v.questionNumber || v.id}</strong>:{" "}
                              {v.name}
                              <div className="quota-summary-details">
                                Will create {getQuestionRowCount(v.id)} columns
                                from survey data
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                    <div className="quota-summary-card">
                      <h5>
                        Nested Columns (
                        {
                          currentQuota.variables.filter(
                            (v) => v.role === "level2"
                          ).length
                        }
                        )
                      </h5>
                      {currentQuota.variables.filter((v) => v.role === "level2")
                        .length === 0 ? (
                        <div className="quota-summary-empty">
                          No nested columns
                        </div>
                      ) : (
                        currentQuota.variables
                          .filter((v) => v.role === "level2")
                          .map((v) => (
                            <div key={v.id} className="quota-summary-item">
                              <strong>{v.questionNumber || v.id}</strong>:{" "}
                              {v.name}
                              <div className="quota-summary-details">
                                Will create nested headers under parent columns
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "configuration" && currentQuota.quotaTable && (
            <div className="quota-table-section">
              <div className="quota-table-controls">
                <div>
                  <h4>{currentQuota.quotaName} Quota Table</h4>
                  <p className="quota-table-description">
                    Enter quota values for each cell. Table generated from
                    survey data rows/columns.
                  </p>
                </div>
                <div className="quota-controls-right">
                  <button
                    onClick={() => setActiveTab("variables")}
                    className="quota-edit-variables-button"
                  >
                    <FontAwesomeIcon icon={faCog} /> Edit Variables
                  </button>
                </div>
              </div>

              <div className="quota-table-container">
                <table className="quota-table">
                  <thead>
                    {/* Main header row */}
                    <tr>
                      <th
                        rowSpan={currentQuota.quotaTable.level2Columns ? 2 : 1}
                        className="quota-table-header-corner"
                      >
                        Rows / Columns
                      </th>

                      {currentQuota.quotaTable.columns.map((column) => {
                        if (
                          currentQuota.quotaTable.level2Columns &&
                          currentQuota.quotaTable.level2Columns[column.id] &&
                          !column.isTotal &&
                          !column.isDefault
                        ) {
                          return (
                            <th
                              key={column.id}
                              colSpan={
                                currentQuota.quotaTable.level2Columns[column.id]
                                  .length
                              }
                              className="quota-table-header-level1"
                            >
                              {column.label}
                              {column.variableLabel && (
                                <div className="quota-variable-source">
                                  ({column.variableLabel})
                                </div>
                              )}
                            </th>
                          );
                        } else {
                          return (
                            <th
                              key={column.id}
                              rowSpan={
                                currentQuota.quotaTable.level2Columns ? 2 : 1
                              }
                              className={`quota-table-header ${
                                column.isTotal || column.isDefault
                                  ? "quota-total-header"
                                  : ""
                              }`}
                            >
                              {column.label}
                              {column.isDefault && (
                                <div className="quota-default-badge">
                                  Default
                                </div>
                              )}
                              {column.variableLabel && !column.isDefault && (
                                <div className="quota-variable-source">
                                  ({column.variableLabel})
                                </div>
                              )}
                            </th>
                          );
                        }
                      })}
                    </tr>

                    {/* Level 2 header row */}
                    {currentQuota.quotaTable.level2Columns && (
                      <tr>
                        {currentQuota.quotaTable.columns.map((column) => {
                          if (column.isTotal || column.isDefault) return null;

                          if (
                            currentQuota.quotaTable.level2Columns[column.id]
                          ) {
                            return currentQuota.quotaTable.level2Columns[
                              column.id
                            ].map((level2Col) => (
                              <th
                                key={`${column.id}_${level2Col.id}`}
                                className="quota-table-header-level2"
                              >
                                {level2Col.label}
                                {level2Col.variableLabel && (
                                  <div className="quota-variable-source">
                                    ({level2Col.variableLabel})
                                  </div>
                                )}
                              </th>
                            ));
                          } else {
                            return (
                              <th
                                key={`${column.id}_placeholder`}
                                className="quota-table-header-level2"
                              ></th>
                            );
                          }
                        })}
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {/* Data rows */}
                    {currentQuota.quotaTable.rows.map((row) => (
                      <tr
                        key={row.id}
                        className={row.isTotal ? "quota-total-row" : ""}
                      >
                        <td
                          className={`quota-row-header ${
                            row.isTotal ? "quota-total-header" : ""
                          }`}
                        >
                          {row.label}
                          {row.variableLabel && !row.isTotal && (
                            <div className="quota-row-variable-label">
                              ({row.variableLabel})
                            </div>
                          )}
                        </td>

                        {currentQuota.quotaTable.columns.map((column) => {
                          if (
                            currentQuota.quotaTable.level2Columns &&
                            currentQuota.quotaTable.level2Columns[column.id] &&
                            !column.isTotal &&
                            !column.isDefault
                          ) {
                            return currentQuota.quotaTable.level2Columns[
                              column.id
                            ].map((level2Col) => {
                              const key = `${row.id}_${level2Col.id}`;
                              return (
                                <td key={key} className="quota-data-cell">
                                  {row.isTotal ? (
                                    <div className="quota-total-cell">
                                      {calculateColumnTotal(level2Col.id)}
                                    </div>
                                  ) : (
                                    <input
                                      type="number"
                                      min="0"
                                      value={
                                        currentQuota.quotaTable.data[key] || 0
                                      }
                                      onChange={(e) =>
                                        handleQuotaValueChange(
                                          row.id,
                                          level2Col.id,
                                          e.target.value
                                        )
                                      }
                                      className="quota-input"
                                    />
                                  )}
                                </td>
                              );
                            });
                          } else {
                            const key = `${row.id}_${column.id}`;
                            return (
                              <td
                                key={key}
                                className={`quota-data-cell ${
                                  column.isTotal || column.isDefault
                                    ? "quota-total-cell"
                                    : ""
                                }`}
                              >
                                {row.isTotal &&
                                (column.isTotal || column.isDefault) ? (
                                  <div className="quota-grand-total">
                                    {calculateGrandTotal()}
                                  </div>
                                ) : row.isTotal ? (
                                  <div className="quota-total-cell">
                                    {calculateColumnTotal(column.id)}
                                  </div>
                                ) : column.isTotal || column.isDefault ? (
                                  <div className="quota-total-cell">
                                    {calculateRowTotal(row.id)}
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    min="0"
                                    value={
                                      currentQuota.quotaTable.data[key] || 0
                                    }
                                    onChange={(e) =>
                                      handleQuotaValueChange(
                                        row.id,
                                        column.id,
                                        e.target.value
                                      )
                                    }
                                    className="quota-input"
                                  />
                                )}
                              </td>
                            );
                          }
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="quota-table-summary">
                <div className="quota-summary-item">
                  <strong>Quota Name:</strong> {currentQuota.quotaName}
                </div>
                <div className="quota-summary-item">
                  <strong>Total Rows:</strong>{" "}
                  {
                    currentQuota.quotaTable.rows.filter((r) => !r.isTotal)
                      .length
                  }
                </div>
                <div className="quota-summary-item">
                  <strong>Total Columns:</strong>{" "}
                  {
                    currentQuota.quotaTable.columns.filter(
                      (c) => !c.isTotal && !c.isDefault
                    ).length
                  }
                </div>
                <div className="quota-summary-item">
                  <strong>Nested Levels:</strong>{" "}
                  {currentQuota.quotaTable.level2Columns
                    ? Object.keys(currentQuota.quotaTable.level2Columns).length
                    : 0}
                </div>
                <div className="quota-summary-item">
                  <strong>Grand Total:</strong>{" "}
                  <span className="grand-total-value">
                    {calculateGrandTotal()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="quota-modal-footer">
          <div className="quota-footer-left">
            <span className="quota-status">
              Currently editing: <strong>{currentQuota.quotaName}</strong> |
              Total Quotas: <strong>{quotaState.quotas.length}</strong>
            </span>
          </div>
          <div className="quota-footer-right">
            <button onClick={onClose} className="quota-cancel-button">
              Cancel
            </button>
            <button onClick={handleSaveAllQuotas} className="quota-save-button">
              Save All Quotas
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotaModal;
